/* eslint-disable react-native/no-inline-styles */
import ResizeObserver from 'resize-observer-polyfill';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { msalInstance, MSAL_SCOPES } from './config/msalConfig';
import { setMsalSession, clearMsalSession, pickAutoSelectAccount } from './config/msalSession';
import { setPersistUserEmail } from './store/persistUserScope';
import { generateTokenInfo } from './reducers/user/utils';

window.ResizeObserver = ResizeObserver;

import React, { useEffect, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { AppRegistry, View, Dimensions } from 'react-native';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import Toast from 'react-native-toast-notifications';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

import store from './store';
import { getAuthInfo, selectUserInfo } from './reducers/user';
import './i18n/index';
import 'rc-slider/assets/index.css';
import i18n from 'i18n-js';

import './styles/web/inputs.css';
import './styles/web/silk.css';
import './components/tours/styles/tour.css';

import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

import { EventProvider } from 'react-native-outside-press';

import WebHeader from './components/webHeader';
import WebDeepLinking from './components/common/webDeepLinking';

import './styles/scrollbar.css';

import Alert from './components/alert';
import Feedback from './components/feedback';
import Popup from './components/popup';
import ImageUpload from './components/imageUpload';
import ProductionOrders from './components/productionOrders/index';
import DownloadConnector from './components/auntification/downloadConnector/index.web';
import EnterEmail from './components/auntification/enterEmail/index';
import Welcome from './components/auntification/welcome/index';
import CreateAccount from './components/auntification/createAccount/index';
import CreatePassword from './components/auntification/createPassword';
import ConnectorEmail from './components/auntification/connectorEmail/index';
import ManualLogin from './components/auntification/manualLogin/index';
import SigninOIDC from './components/auntification/signinOIDC/index';
import EnterName from './components/auntification/enterName/index';
import ForgotPass from './components/auntification/forgotPass/index';
import ConfirmCode from './components/auntification/confirmCode';
import ConfirmInvite from './components/auntification/confirmCode';
import FactoryTime from './components/auntification/factoryTime';
import FactoryLanguage from './components/auntification/factoryLanguage';
import QR from './components/auntification/qr/index.web';
import Planboard from './components/v2/pages/planboard';

import MachineDetailsSecret from './components/machineDetails';
import AddDevice from './components/settings/components/pages/addDevice';

import ClampingTool from './components/clampingTool/index';
import NcFiles from './components/ncFiles/ncFilesList';
import Wallboard from './components/wallboard';
import Details from './components/machines/machineDetails';
import OperatorPanel from './components/operatorPanel/index';
import Loading from './components/loading';
import DevInfo from './components/devInfo';
import StressTest from './components/devInfo/stressTestPage';
import EnvironmentDetails from './components/devInfo/environmentDetails';
import CropperWeb from './components/v2/components/imageHandling/components/Cropper/components/CropperWeb';
import ProductionInformation from './components/productionInformation/index.web';
import ImageSystemInfo from './components/imageSystemInfo/index';
import NCFile from './components/ncFile';
import NCFileVersions from './components/ncFileVersions';
import SearchList from './components/searchList';
import Search from './components/search';
import FieldInformation from './components/fieldInformation';
import ConnectorSetup from './components/connectorSetup/index.web';
import ConnectorLinked from './components/connectorLinked';
import ToolsDetails from './components/toolsDetails';

import Settings from './components/settings/index';

import OnHold from './components/tenantStatus/onHold';
import Deactivated from './components/tenantStatus/deactivated';
import SettingsLimited from './components/tenantStatus/settingsLimited';
import OnboardingWizard from './components/tenantStatus/onboardingWizard';

import SignalRSystem from './components/signalRSystem';
import SignalRDebugger from './components/signalRDebugger';
import { FeatureFlagsAvailable } from './components/featureFlags';

import NotificationSystem from './components/notificationSystem';
import UploadQueue from './components/uploadQueue';

import PanelView from './components/panelView';
import PanelViewProperties from './components/panelViewProperties';

import ProductionConnectorSetup from './components/settings/v2/structure/connector/productionConnectorSetup/index';

import ImageHandlingV2 from './components/v2/components/imageHandling';

import { LicenseInfo } from '@mui/x-license';
LicenseInfo.setLicenseKey(process.env.MUI_X_LICENSE_KEY);

import { selectedZoomOPIndex, setConfig } from './components/frame';
import { initFontConfig } from './styles/fonts';
import Svg from './styles/icons/svg';
import { ThemeProvider } from './styles/colors';

import { navigationRef, isReadyRef, updateIsPhone, bottomAnimationConfig, setSecureValue, getSecureValue, setLocale, isPhone } from './helpers';
import { MachinesNavigation } from './components/v2/pages/machines/navigation/MachinesNavigation';
import { machinesLinking } from './components/v2/pages/machines/navigation/config';
import { batchDetailsLinking, BatchDetailsScreenName } from './components/v2/pages/batch/details/navigation/config';
import { ncFileDetailsLinking, NCFileDetailsScreenName } from './components/v2/pages/nc-file/details/navigation/config';
import { partDetailsLinking, PartDetailsScreenName } from './components/v2/pages/part/details/navigation/config';
import { toolsDetailsLinking, ToolsDetailsScreenName } from './components/v2/pages/tools/details/navigation/config';
import {
  ALL_MACHINES_OBJECT,
  BOTTOM_SHEET_OPTIONS,
  NAVIGATION_CONFIG,
  SMART_FACTORY,
  WebDetailsScreenName,
} from './components/v2/pages/common/constants/base.constants';
import { jobDetailsLinking, JobDetailsScreenName } from './components/v2/pages/job/navigation/config';
import { setSelectedMachine } from './reducers/machines';
import { initTracking, trackAppOpen } from './services/tracking';
import { sendLog, startMemoryReporter } from './reducers/dataDogs';
import { LOGGER_TITLES } from './reducers/dataDogs/constants';
import { Host as PortalProvider } from 'react-native-portalize';
import { TourProvider } from './components/tours';
import { ErrorBoundary, RootErrorFallback, setupGlobalErrorHandlers } from './components/v2/components/errorBoundary';
import { operatorPanelLinking, operatorPanelScreens } from './components/operatorPanel/navigation';
import { productionOrderDetailsLinking, ProductionOrderDetailsScreenName } from './components/v2/pages/productionOrder/navigation/config';
import { WebDetailsNavigator } from './components/v2/pages/common/navigation/WebDetailsNavigator';
import { PERFORMANCE_MAIN_SCREEN, performanceLinking } from './components/v2/pages/performance/navigation';
import { PerformanceWebNavigation } from './components/v2/pages/performance/navigation/PerformanceWebNavigation';
import { dashboardLinking, DashboardScreenName } from './components/v2/pages/dashboard/navigation/config';
import { DashboardNavigator } from './components/v2/pages/dashboard/navigation/DashboardNavigator';
import { filterScreensByFeatureFlags } from './config/navigationRestrictions/helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

console.disableYellowBox = true;

setupGlobalErrorHandlers();

const Stack = createStackNavigator();

const linking = {
  prefixes: ['https://smarfac.com/', 'smartfactory://', 'http://localhost:3030/'],
  config: {
    screens: {
      Settings: {
        path: 'settings',
        screens: {
          Account: {
            screens: {
              AccountScreen: 'account',
              AccountDetails: 'details',
            },
          },
          Users: {
            screens: {
              UsersScreen: 'users',
              UserDetails: 'users/:id',
            },
          },
          Devices: {
            screens: {
              DevicesScreen: 'devices',
              DeviceDetails: 'devices/:id',
            },
          },
          Machines: {
            screens: {
              MachinesScreen: {
                screens: {
                  MachinesList: 'machines/list',
                  MachinesGroup: 'machines/group',
                  MachinesOperations: 'machines/operations',
                  MachinesImnoo: 'machines/imnoo',
                },
              },
              MachinesImnooDetails: 'machines/imnoo/:machineName',
              MachineDetails: 'machines/:id',
              MachineCountSettings: 'machines/:id/count-settings',
              MachineConnection: 'machines/:id/connection',
              MachineAssignedOperations: 'machines/:id/operations',
              MachinesGroupDetails: 'machines/group/:id',
              MachinesOperationDetails: 'machines/operations/:id',
            },
          },
          Connector: {
            screens: {
              ConnectorScreen: 'connectors',
              FileConnector: 'connectors/file',
              FileConnectorDetails: 'connectors/file/details',
            },
          },
          ['API Keys']: {
            screens: {
              APIKeysScreen: 'api-keys',
            },
          },
          Imnoo: {
            screens: {
              ImnooScreen: 'imnoo',
            },
          },
          Company: {
            screens: {
              CompanyScreen: 'company',
            },
          },
          Notifications: {
            screens: {
              NotificationsScreen: 'notifications',
              EmailReports: 'notifications/reports',
            },
          },
          Support: {
            screens: {
              SupportScreen: 'support',
            },
          },
          ReInvite: 're-invite',
          Tools: 'tools',
          LinkConnector: 'link-connector',
        },
      },
      ToolsDetails: 'tools-details',
      AddDevice: 'settings/devices/add',
      Authorized: machinesLinking,
      Wallboard: 'wallboard',
      Planboard: 'planboard',
      ConnectorEmail: 'connector',
      Welcome: 'welcome',
      ScanQR: 'scan-qr',
      Login: 'login',
      EnterName: 'registration',
      Details: 'details',
      ProductionOrders: 'production-orders',
      OperatorPanel: 'operator-panel',
      ...operatorPanelLinking,
      DevInfo: 'dev-info',
      StressTest: 'stress-test',
      FieldInformation: 'field-information',
      PanelView: 'panel-view',
      PanelViewProperties: 'panel-view-properties',
      PanelViewFromBottom: 'panel-view-bottom',
      CropImage: 'crop-image',
      ImageHandling: 'image-handling',
      ImageSystemInfo: 'system-info',
      ProductionInformation: 'production-information',
      EnvironmentDetails: 'environment-details',
      ClampingTool: 'clamping-tool',
      NCFile: 'nc-file',
      NCFileVersions: 'nc-file-versions',
      Search: 'search',
      SearchList: 'search-list',
      NCFileDetails: 'nc-file-details',
      ProductionBatch: 'production-batch',
      Part: 'part',
      MachineDetailsSecret: 'machine-details-secret',
      NcFiles: 'nc-files',
      ConnectorSetup: 'connect',
      ConnectorLinked: 'connector-linked',
      CreateAccount: 'user-details',
      CreatePassword: 'user-password',
      EnterEmail: 'user-email',
      ForgotPass: 'forgot-password',
      ConfirmCode: 'confirm-code',
      ConfirmInvite: 'confirm-invite',
      FactoryTime: 'factory-time',
      FactoryLanguage: 'factory-language',
      QR: 'qr',
      DownloadConnector: 'download-connector',
      SigninOIDC: 'signin-oidc',
      OnHold: 'on-hold',
      OnboardingWizard: 'onboarding-wizard',
      Deactivated: 'deactivated',
      SettingsLimited: {
        path: 'settings-limited',
        screens: {
          Account: 'account',
          AccountDetails: 'account/details',
          Users: 'users',
          UserDetails: 'users/:id',
          Connector: 'connector',
          FileConnector: 'connector/file',
          FileConnectorDetails: 'connector/file/:id',
          Company: 'company',
          Support: 'support',
        },
      },
      ProductionConnectorSetup: 'production-connector-setup',
      [WebDetailsScreenName]: {
        path: 'info',
        screens: {
          [BatchDetailsScreenName]: batchDetailsLinking,
          [NCFileDetailsScreenName]: ncFileDetailsLinking,
          [PartDetailsScreenName]: partDetailsLinking,
          [JobDetailsScreenName]: jobDetailsLinking,
          [ProductionOrderDetailsScreenName]: productionOrderDetailsLinking,
          [ToolsDetailsScreenName]: toolsDetailsLinking,
        },
      },
      [PERFORMANCE_MAIN_SCREEN]: performanceLinking,
      [DashboardScreenName]: dashboardLinking,
    },
  },
};

const connectorSetupPath = linking?.config?.screens?.ConnectorSetup;

const isConnectorSetupRouteActive = () => {
  if (typeof window === 'undefined' || !connectorSetupPath) {
    return false;
  }

  const currentPath = (window.location?.pathname || '').toLowerCase();
  const normalizedRoute = `/${connectorSetupPath.toLowerCase()}`;

  return currentPath.startsWith(normalizedRoute);
};

const components = [
  {
    name: 'ConnectorSetup',
    Component: ConnectorSetup,
    title: 'Connector Setup',
  },
  {
    name: 'CreatePassword',
    Component: CreatePassword,
    title: 'Create Password',
  },
  {
    name: 'ConfirmInvite',
    Component: ConfirmInvite,
    title: 'Confirm Invite',
  },
  {
    name: 'FactoryTime',
    Component: FactoryTime,
    title: 'Factory Time',
  },
  {
    name: 'FactoryLanguage',
    Component: FactoryLanguage,
    title: 'Factory Language',
  },
  {
    name: 'ConnectorLinked',
    Component: ConnectorLinked,
    title: 'Connector Linked',
  },
  {
    name: 'Loader',
    Component: Loading,
    title: 'Loading',
  },
  {
    name: 'QR',
    Component: QR,
    title: 'Smart Factory',
  },
  {
    name: 'ForgotPass',
    Component: ForgotPass,
    title: 'Forgot Password',
  },
  {
    name: 'ConfirmCode',
    Component: ConfirmCode,
    title: 'Enter Code',
  },
  {
    name: 'EnterEmail',
    Component: EnterEmail,
    title: 'Login',
  },
  {
    name: 'ConnectorEmail',
    Component: ConnectorEmail,
    title: 'Connector',
  },
  {
    name: 'Welcome',
    Component: Welcome,
    title: 'Login',
  },
  {
    name: 'Login',
    Component: ManualLogin,
    title: 'Login',
  },
  {
    name: 'SigninOIDC',
    Component: SigninOIDC,
    title: 'Signin',
  },
  {
    name: 'CreateAccount',
    Component: CreateAccount,
    title: 'Create account',
  },
  {
    name: 'EnterName',
    Component: EnterName,
    title: 'Create account',
  },
  {
    name: 'DownloadConnector',
    Component: DownloadConnector,
    title: 'Download Connector',
  },
  {
    name: 'OnHold',
    Component: WebHeader(OnHold),
    title: 'On Hold',
  },
  {
    name: 'OnboardingWizard',
    Component: WebHeader(OnboardingWizard),
    title: 'Onboarding',
  },
  {
    name: 'Deactivated',
    Component: WebHeader(Deactivated),
    title: 'Deactivated',
  },
  {
    name: 'SettingsLimited',
    Component: WebDeepLinking(WebHeader(SettingsLimited)),
    title: 'Settings',
  },
];

const authorizedOnly = [
  {
    name: 'Authorized',
    Component: WebHeader(MachinesNavigation),
    title: 'Machines',
  },
  {
    name: 'OperatorPanel',
    Component: OperatorPanel,
    title: 'Operator panel',
    options: BOTTOM_SHEET_OPTIONS,
  },
  ...operatorPanelScreens,
  {
    name: 'DevInfo',
    Component: WebHeader(DevInfo),
    title: 'Developer information',
  },
  {
    name: 'StressTest',
    Component: WebHeader(StressTest),
    title: 'Network Stress Test',
  },
  {
    name: 'FieldInformation',
    Component: WebHeader(FieldInformation),
    title: 'Field information',
  },
  {
    name: 'Settings',
    Component: WebDeepLinking(WebHeader(Settings)),
    title: 'Settings',
  },
  {
    name: 'ProductionOrders',
    Component: WebDeepLinking(WebHeader(ProductionOrders)),
    title: 'Production orders',
  },
  {
    name: 'NcFiles',
    Component: WebHeader(NcFiles),
    title: 'NС Files',
  },
  {
    name: 'ToolsDetails',
    Component: WebHeader(ToolsDetails),
    title: 'Tools Details',
  },
  {
    name: 'ClampingTool',
    Component: WebHeader(ClampingTool),
    title: 'ClampingTool',
  },
  {
    name: 'Search',
    Component: WebHeader(Search),
    title: `Search - ${SMART_FACTORY}`,
  },
  {
    name: 'SearchList',
    Component: WebHeader(SearchList),
    title: 'Search',
  },
  {
    name: 'Wallboard',
    Component: WebDeepLinking(WebHeader(Wallboard)),
    title: 'Wallboard',
  },
  {
    name: 'Planboard',
    Component: WebHeader(Planboard),
  },
  {
    name: 'Details',
    Component: WebHeader(Details),
    title: 'Machine details',
  },
  {
    name: 'MachineDetailsSecret',
    Component: WebHeader(MachineDetailsSecret),
    title: 'Machine Details',
  },
  {
    name: 'ImageHandling',
    Component: ImageHandlingV2,
    title: 'Image Handling',
  },
  {
    name: 'EnvironmentDetails',
    Component: EnvironmentDetails,
  },
  {
    name: 'ImageSystemInfo',
    Component: ImageSystemInfo,
    title: 'Image System Info',
  },
  {
    name: 'CropImage',
    Component: CropperWeb,
    title: 'Crop Image',
  },
  {
    name: 'PanelView',
    Component: PanelView,
    title: 'Panel View',
  },
  {
    name: 'Part',
    Component: WebHeader(PanelViewProperties),
    title: 'Part',
  },
  {
    name: 'ProductionBatch',
    Component: WebHeader(PanelViewProperties),
    title: 'Production Batch',
  },
  {
    name: 'NCFileDetails',
    Component: WebHeader(PanelViewProperties),
    title: 'NC File',
  },
  {
    name: 'PanelViewFromBottom',
    Component: PanelView,
    options: bottomAnimationConfig,
    title: 'Panel View',
  },
  {
    name: 'NCFile',
    Component: WebHeader(NCFile),
    title: 'NC File',
  },
  {
    name: 'NCFileVersions',
    Component: WebHeader(NCFileVersions),
    title: 'NC File Versions',
  },
  {
    name: 'ProductionInformation',
    Component: WebHeader(ProductionInformation),
    title: 'Production Information',
  },
  {
    name: 'AddDevice',
    Component: WebHeader(AddDevice),
    title: 'Add device',
  },
  {
    name: 'ProductionConnectorSetup',
    Component: ProductionConnectorSetup,
    title: 'ProductionConnectorSetup',
    presentation: 'transparentModal',
  },
  {
    name: WebDetailsScreenName,
    Component: WebHeader(WebDetailsNavigator),
    title: 'Details',
    presentation: 'transparentModal',
    options: {
      detachPreviousScreen: false,
      gestureEnabled: false,
      animationEnabled: false,
      cardStyle: { backgroundColor: 'transparent' },
    },
  },
  {
    name: PERFORMANCE_MAIN_SCREEN,
    Component: WebHeader(PerformanceWebNavigation),
    title: 'Performance',
    presentation: 'transparentModal',
    options: {
      detachPreviousScreen: false,
      gestureEnabled: false,
      animationEnabled: false,
      cardStyle: { backgroundColor: 'transparent' },
    },
  },
  {
    name: DashboardScreenName,
    Component: WebHeader(DashboardNavigator),
    title: 'Dashboard',
  },
];

const App = ({ init, lg, updateLanguage, updateAppValue, updateAppView, tokenState, updateTokenState }) => {
  const dispatch = useAppDispatch();
  const featureFlags = useSelector((state) => state.user?.featureFlags);
  const userInfo = useSelector((state) => state.user?.userInfo);
  const isConnectorRouteActive = isConnectorSetupRouteActive();
  const shouldRenderAuthorizedContent = tokenState && !isConnectorRouteActive;
  const isSignalRDebuggerEnabled = FeatureFlagsAvailable('general-signalr-debugger', featureFlags);
  const screens = shouldRenderAuthorizedContent ? filterScreensByFeatureFlags([...authorizedOnly, ...components], featureFlags) : components;

  useEffect(() => {
    if (shouldRenderAuthorizedContent && !appAuthorizedLogged) {
      appAuthorizedLogged = true;
      dispatch(sendLog({ title: LOGGER_TITLES.APP_BOOT_AUTHORIZED, data: { tMs: Math.round(performance.now()) }, status: 'debug' }));
    }
  }, [shouldRenderAuthorizedContent, dispatch]);

  useEffect(() => {
    if (shouldRenderAuthorizedContent) {
      initTracking().finally(() => {
        trackAppOpen(userInfo);
      });
      startMemoryReporter();
    }
  }, [shouldRenderAuthorizedContent]);

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      fallback={<Loading />}
      onReady={() => {
        isReadyRef.current = true;
        if (isPhone) {
          dispatch(setSelectedMachine(ALL_MACHINES_OBJECT));
        }
        if (shouldRenderAuthorizedContent && !navReadyLogged) {
          navReadyLogged = true;
          dispatch(sendLog({ title: LOGGER_TITLES.APP_BOOT_NAVIGATION_READY, data: { tMs: Math.round(performance.now()) }, status: 'debug' }));
        }
      }}
    >
      {shouldRenderAuthorizedContent && <UploadQueue />}
      {shouldRenderAuthorizedContent && (
        <SignalRSystem {...{ updateAppView, getMessaging, getToken, onMessage, isSupported, initializeApp, lg, updateLanguage, useMsal }} />
      )}
      {shouldRenderAuthorizedContent && isSignalRDebuggerEnabled && <SignalRDebugger />}
      <Stack.Navigator initialRouteName={init} screenOptions={NAVIGATION_CONFIG}>
        {screens.map((item) => (
          <Stack.Screen name={item.name} options={{ title: item.title, presentation: item.presentation, ...item.options }} key={item.name}>
            {(props) => (
              <ErrorBoundary screenName={item.name} resetKey={props.route?.key}>
                <item.Component
                  {...{
                    ...props,
                    lg,
                    updateLanguage,
                    updateAppValue,
                    updateAppView,
                    updateTokenState,
                    useMsal,
                  }}
                />
              </ErrorBoundary>
            )}
          </Stack.Screen>
        ))}
      </Stack.Navigator>
    </NavigationContainer>
  );
};

let timeout;
let inited = false;
let appAuthorizedLogged = false;
let navReadyLogged = false;

// Populates Redux `user.userInfo` (via getAuthInfo) and secure storage identity
// fields (via generateTokenInfo) in parallel. Shared by the three init paths:
// B2C redirect, silent-restore, and the startup backfill invariant.
const hydrateIdentity = async (token, tag) => {
  try {
    await Promise.all([generateTokenInfo(token), store.store.dispatch(getAuthInfo())]);
    return true;
  } catch (e) {
    console.error(`[init] identity population failed (${tag})`, e);
    return false;
  }
};

const RootComponent = (props) => {
  const [updateAppValue, setUpdate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState({ ready: false, isToken: false });
  const [tokenState, setTokenStateRaw] = useState(null);
  // flushSync forces React to commit the tokenState update before returning, so
  // callers that do `updateTokenState(t); navigateTo('Authorized');` don't race
  // the re-render. Without it, `navigateTo` would read a stale `routeNames`
  // (before Authorized was registered via shouldRenderAuthorizedContent) and
  // the navigate dispatch would be silently dropped. This closes the window at
  // the source so every caller gets correct behavior without individual fixes.
  const updateTokenState = useCallback((value) => {
    flushSync(() => setTokenStateRaw(value));
  }, []);
  const [zoomOPIndex, setZoomOPIndex] = useState(3);
  const [lg, setLg] = useState('en');
  const isConnectorRouteActive = isConnectorSetupRouteActive();

  const updateAppView = (value) => {
    setUpdate(value || updateAppValue + 1);
  };

  const updateLanguage = async (language) => {
    setLocale(language);
    i18n.locale = language;
    setLg(language);
    setSecureValue('language', language);
  };

  const onChange = () => {
    const width = Dimensions.get('window').width;
    const height = Dimensions.get('window').height;
    updateIsPhone(width);

    setConfig(width, height, selectedZoomOPIndex);
    initFontConfig(false, true);

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      updateAppView(new Date().getTime());
    }, 10);
  };

  useEffect(() => {
    (async () => {
      const zoomOPIndexFromLocalStorage = getSecureValue('zoomOPIndex');
      const isZoomOPIndexSavedInLocalStorage =
        zoomOPIndexFromLocalStorage != null && typeof zoomOPIndexFromLocalStorage != 'boolean' && isFinite(zoomOPIndexFromLocalStorage);
      if (isZoomOPIndexSavedInLocalStorage) {
        setZoomOPIndex(+zoomOPIndexFromLocalStorage);
      }
      const width = Dimensions.get('window').width;
      const height = Dimensions.get('window').height;
      setConfig(width, height, isZoomOPIndexSavedInLocalStorage ? +zoomOPIndexFromLocalStorage : zoomOPIndex);
      setSecureValue('status', 'active');
      const l = getSecureValue('language');
      if (l) {
        updateLanguage(l);
      }
      setLocale(l || 'en');

      // Set initial env from subdomain BEFORE MSAL handling, so detectEnvironment()
      // has a value for the first API call (generateTokenInfo). setMsalSession will
      // override this with the JWT claim env, and generateTokenInfo will re-validate from backend.
      const host = window.location.host;
      const subdomain = host.split('.')[0];
      const envList = {
        dev: 'dev',
        qa: 'qa',
        preview: 'stg',
        smarfac: 'prod',
        www: 'prod',
        app: 'prod',
        'localhost:3030': process.env.ENV || 'dev',
      };
      const currentEnv = getSecureValue('env');
      if (!currentEnv) {
        setSecureValue('env', envList[subdomain] || '');
      }

      // Process any pending B2C redirect BEFORE MsalProvider mounts.
      // This runs in RootComponent's useEffect (before token.ready=true),
      // so MsalProvider hasn't mounted yet and can't race with us.
      let redirectResponse = null;
      try {
        redirectResponse = await msalInstance.handleRedirectPromise();
      } catch (e) {
        // Redirect processing failed — fall through to session restore
      }

      if (redirectResponse) {
        // Returning from a successful B2C login redirect.
        // setMsalSession extracts env from JWT claims for immediate correct routing.
        setMsalSession(redirectResponse.account, redirectResponse.accessToken);
        // On failure, clear so the user lands on login instead of a zombie Authorized with empty profile.
        if (!(await hydrateIdentity(redirectResponse.accessToken, 'redirect path'))) {
          clearMsalSession();
        }
      }

      const savedId = getSecureValue('_sf_active_account_id');
      let activeAccount = savedId ? msalInstance.getAccountByHomeId(savedId) : null;

      // Cross-tab invariant: MSAL accounts live in shared localStorage, savedId in per-tab
      // sessionStorage. If another tab removed this account, savedId still points at it —
      // clear the stale session so we don't enter Authorized with a token whose account is gone.
      if (savedId && !activeAccount) {
        console.warn('[init] active account no longer in MSAL cache — clearing stale session');
        clearMsalSession();
      }

      // Fresh tab (no per-tab session): fall back to MSAL's active account or the
      // single cached account. Only runs when savedId was never
      // set — if clearMsalSession just ran above for a stale savedId we skip this
      // and let the user go through the chooser / login rather than silently
      // picking another account.
      if (!savedId) {
        activeAccount = pickAutoSelectAccount();
      }

      if (activeAccount) {
        msalInstance.setActiveAccount(activeAccount);
        setPersistUserEmail(activeAccount.username);
      }

      let t = getSecureValue('token');

      // Fresh tab: no token in storage but MSAL has cached accounts — acquire silently
      if (!t && activeAccount) {
        try {
          const tokenData = await msalInstance.acquireTokenSilent({ scopes: MSAL_SCOPES, account: activeAccount });
          t = tokenData.accessToken;
          // Extract JWT env/name claims — otherwise the subdomain-derived env wins
          // for any user whose JWT claims a different env (e.g. QA user on localhost).
          setMsalSession(activeAccount, t);
          // Identity population failed — clear rather than render Authorized with userInfo=null.
          if (!(await hydrateIdentity(t, 'silent restore'))) {
            clearMsalSession();
            t = null;
          }
        } catch (e) {
          // Silent acquisition failed — user will be redirected to login
        }
      }

      // Heals tabs whose sessionStorage has a token but Redux userInfo is empty —
      // e.g. a prior backendCall-fallback hijack, or a SigninOIDC redirect that
      // skipped the identity population above.
      if (t && !selectUserInfo(store.store.getState())?.emailAddress) {
        if (!(await hydrateIdentity(t, 'backfill'))) {
          clearMsalSession();
          t = null;
        }
      }

      updateTokenState(t);
      setToken({ ready: true, isToken: Boolean(t) });
    })();

    const sub = Dimensions.addEventListener('change', onChange);
    return () => {
      isReadyRef.current = false;
      if (sub) {
        sub.remove();
      } else {
        Dimensions.removeEventListener('change', onChange);
      }
    };
  }, []);

  if (loading || !token.ready) {
    return (
      <ThemeProvider>
        <ErrorBoundary screenName="RootBoot" FallbackComponent={RootErrorFallback}>
          <View
            style={{ flex: 1, width: '100%', height: '100%' }}
            onLayout={(event) => {
              var { width, height } = event.nativeEvent.layout;
              updateIsPhone(width);
              if (!inited) {
                setTimeout(() => {
                  initFontConfig(); // init font for entire app except Operator Panel
                  initFontConfig(false, true); // init font for Operator Panel
                  setLoading(false);
                  inited = true;
                }, 100);
              }
            }}
          >
            <Loading />
          </View>
        </ErrorBoundary>
      </ThemeProvider>
    );
  }

  const initialRoute = isConnectorRouteActive ? 'ConnectorSetup' : tokenState ? 'Authorized' : 'Login';

  return (
    <ThemeProvider>
      <ErrorBoundary screenName="RootBoot" FallbackComponent={RootErrorFallback}>
        <Provider store={store.store}>
          <PersistGate loading={null} persistor={store.persistor}>
            <NotificationSystem />
            <EventProvider style={{ flex: 1 }}>
              <MsalProvider instance={msalInstance}>
                <PortalProvider>
                  <TourProvider>
                    <ErrorBoundary screenName="Root" FallbackComponent={RootErrorFallback}>
                      <App
                        {...{
                          init: initialRoute,
                          lg,
                          updateLanguage,
                          updateAppValue,
                          updateAppView,
                          tokenState,
                          updateTokenState,
                        }}
                      />
                    </ErrorBoundary>
                  </TourProvider>
                </PortalProvider>
              </MsalProvider>
            </EventProvider>
            <Toast
              ref={(ref) => (global['toast'] = ref)}
              textStyle={{ textAlign: 'center' }}
              successIcon={<Svg icon="check" width={15} height={15} color="Label/Light/AlwaysLight Primary" />}
              dangerIcon={<Svg icon="error" width={15} height={15} color="Button/Light/Primary Text" />}
            />
            <Alert ref={(ref) => (global['systemAlert'] = ref)} />
            <Feedback ref={(ref) => (global['systemFeedback'] = ref)} />
            <Popup
              ref={(ref) => (global['systemPopup'] = ref)}
              {...{
                lg,
                updateLanguage,
                updateAppView,
              }}
            />
            <ImageUpload ref={(ref) => (global['systemImageUpload'] = ref)} />
          </PersistGate>
        </Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

const fonts = [
  'https://fonts.googleapis.com/css2?family=Roboto:wght@100;200;300;400;500;600;700;800;900&display=swap',
  'https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap',
];

fonts.forEach((link) => {
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = link;
  document.head.appendChild(fontLink);
});

AppRegistry.registerComponent('RootComponent', () => RootComponent);

AppRegistry.runApplication('RootComponent', {
  rootTag: document.getElementById('root'),
});
